import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { getActiveAnnouncements } from "../api/requests/announcement-requests"
import {
  ANNOUNCEMENT_REFRESH_INTERVAL,
  ANNOUNCEMENT_ROTATION_INTERVAL,
} from "../constants/announcements"

/**
 * Panel duyuru şeridinin verisi + rotasyon durumu.
 *
 * Şeridin tüm zamanlama mantığı burada durur; bileşen yalnız çizim yapar.
 * Böylece rotasyonu bileşeni render etmeden test edebiliyoruz.
 *
 * Davranış:
 *   0 duyuru → boş, bileşen hiç çizilmez
 *   1 duyuru → sabit gösterilir, interval AÇILMAZ
 *   2+       → ANNOUNCEMENT_ROTATION_INTERVAL'de bir sıradaki, sonda başa döner
 */
export const useAnnouncements = () => {
  const [items, setItems] = useState([])
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  // Kayan (uzun) metin bitene kadar rotasyon askıya alınır: süre metnin
  // uzunluğuna göre değiştiği için sabit aralıkla yarışmamalı.
  const [holdRotation, setHoldRotation] = useState(false)

  // Bileşen söküldükten sonra gelen yanıtın setState çağırmasını engeller.
  const mountedRef = useRef(true)

  const load = useCallback(async () => {
    try {
      const json = await getActiveAnnouncements()
      if (!mountedRef.current) return
      setItems(Array.isArray(json?.data) ? json.data : [])
    } catch {
      // Duyuru şeridi ikincil bir bilgi alanı: istek düşerse panel sessizce
      // şeritsiz çalışmalı, kullanıcıya hata göstermenin bir karşılığı yok.
      if (mountedRef.current) setItems([])
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    load()

    // Süper admin yeni duyuru yayınladığında panel tam sayfa yenileme
    // beklemeden görsün diye periyodik tazeleme. PlanFeatureContext'teki
    // "her rota değişiminde çek" deseni burada kasten kullanılmadı: şerit
    // sayfadan bağımsız, her gezinmede istek atmasının karşılığı yok.
    const refreshId = setInterval(load, ANNOUNCEMENT_REFRESH_INTERVAL)

    return () => {
      mountedRef.current = false
      clearInterval(refreshId)
    }
  }, [load])

  // Liste değişince (duyuru eklendi/çıktı) baştan başla — aksi hâlde index
  // kısalmış listenin dışında kalabilir ya da kullanıcı ortadan devam eder.
  useEffect(() => {
    setIndex(0)
  }, [items.length])

  useEffect(() => {
    // Tek duyuru varsa dönecek bir şey yok: interval hiç açılmıyor.
    // holdRotation: şeritte kayan metin sürüyor, sırayı o bitirince verecek.
    if (items.length < 2 || paused || holdRotation) return

    const rotationId = setInterval(() => {
      setIndex(prev => (prev + 1) % items.length)
    }, ANNOUNCEMENT_ROTATION_INTERVAL)

    return () => clearInterval(rotationId)
  }, [items.length, paused, holdRotation])

  // Sekme arka plandayken saymanın anlamı yok; kullanıcı geri döndüğünde
  // biriken geçişleri toplu görmesin diye duraklatılır.
  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden)
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [])

  const goTo = useCallback(
    (next) => {
      setIndex(() => {
        if (!items.length) return 0
        // JS'te -1 % 3 === -1; negatif index'i sarmak için iki kez modülo.
        return ((next % items.length) + items.length) % items.length
      })
    },
    [items.length]
  )

  // index her zaman sınır içinde okunur: liste kısalırsa render patlamaz.
  const current = useMemo(
    () => (items.length ? items[index % items.length] : null),
    [items, index]
  )

  return {
    items,
    current,
    index: items.length ? index % items.length : 0,
    goTo,
    paused,
    holdRotation,
    setHoldRotation,
    pause: useCallback(() => setPaused(true), []),
    resume: useCallback(() => setPaused(false), []),
  }
}
